import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import LensIntelligenceShell from '../LensIntelligenceShell';
import AskAiChooseContextModal from '../AskAiChooseContextModal';
import IntelligenceInsightsNav, { OVERVIEW_KEY } from './IntelligenceInsightsNav';
import IntelligenceInsightsOverview from './IntelligenceInsightsOverview';
import IntelligenceInsightsHighlights from './IntelligenceInsightsHighlights';
import IntelligenceInsightsManagementSummary from './IntelligenceInsightsManagementSummary';
import IntelligenceInsightsKeyFindings from './IntelligenceInsightsKeyFindings';
import IntelligenceInsightsSwot from './IntelligenceInsightsSwot';
import IntelligenceInsightsInvestmentOpinion from './IntelligenceInsightsInvestmentOpinion';
import IntelligenceInsightsRemediationInvestment from './IntelligenceInsightsRemediationInvestment';
import IntelligenceInsightsRisksSignals from './IntelligenceInsightsRisksSignals';
import IntelligenceInsightsEvidenceFollowUps from './IntelligenceInsightsEvidenceFollowUps';
import InsightsGeneratingOverlay from './InsightsGeneratingOverlay';
import { InsightsReadOnlyProvider } from './InsightsReadOnlyContext';
import { INSIGHT_MODULES, OVERVIEW_VARIANT_OPTIONS } from './overviewDummyData';
import { useIntelligenceInsightsOverview } from '../../../hooks/useIntelligenceInsightsOverview';
import { useIntelligenceInsightsHighlights } from '../../../hooks/useIntelligenceInsightsHighlights';
import { useIntelligenceInsightsModule } from '../../../hooks/useIntelligenceInsightsModule';
import { CONTEXT_MODE, useLensIntelligenceScope } from '../../../hooks/useLensIntelligenceScope';
import { workstreamOptionsFromList } from '../../../utils/aiWardenScope';
import { buildInsightsAnalysisContext } from '../../../utils/intelligenceInsightsContext';
import { buildInsightsAskLensOrigin } from '../../../utils/intelligenceInsightsAskLensOrigin';
import { clearAskAiChat, setInsightsAskOrigin } from '../../../redux/intelligenceSlice';
import { exportIntelligenceInsightsPdf, canExportInsightsModule } from '../../../utils/intelligenceInsightsExportPdf';
import { buildSourceReadinessReviewFocus } from '../../../utils/intelligenceSourceReadiness';
import styles from '../intelligenceInsights.module.css';

const HIGHLIGHTS_KEY = 'highlights';

const API_MODULES = {
    management_summary: IntelligenceInsightsManagementSummary,
    key_findings: IntelligenceInsightsKeyFindings,
    swot_analysis: IntelligenceInsightsSwot,
    investment_opinion: IntelligenceInsightsInvestmentOpinion,
    remediation_investment: IntelligenceInsightsRemediationInvestment,
    risks_signals: IntelligenceInsightsRisksSignals,
    evidence_follow_ups: IntelligenceInsightsEvidenceFollowUps,
};

/**
 * Intelligence Insights — Overview + all seven modules wired to Insights V2 APIs.
 */
const IntelligenceInsights = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const projectId = selectedProject?.id;
    const indexingFiles = useSelector(state => state?.documents?.indexingFiles || []);
    const [selectedKey, setSelectedKey] = useState(OVERVIEW_KEY);
    const [moduleFocus, setModuleFocus] = useState(null);
    const [overviewFocus, setOverviewFocus] = useState(null);
    const [contextModalOpen, setContextModalOpen] = useState(false);
    const [historicalView, setHistoricalView] = useState(null);

    const readOnly = Boolean(historicalView?.analysisId);
    const historicalAnalysisId = historicalView?.analysisId || null;

    const {
        contextMode,
        setContextMode,
        selectedWorkstreams,
        setSelectedWorkstreams,
        customContext,
        setCustomContext,
        workstreams,
        effectiveWorkstreamIds,
        scopeStats,
    } = useLensIntelligenceScope(projectId);

    const analysisContext = useMemo(
        () =>
            buildInsightsAnalysisContext(
                contextMode,
                selectedWorkstreams,
                effectiveWorkstreamIds,
            ),
        [contextMode, selectedWorkstreams, effectiveWorkstreamIds],
    );

    const {
        overviewData,
        payload: overviewPayload,
        loading,
        generating,
        error,
        generateAnalysis,
        demoVariant,
        setDemoVariant,
        demoMode,
        fetchOverview,
    } = useIntelligenceInsightsOverview(projectId, analysisContext);

    const highlightsEnabled = selectedKey === HIGHLIGHTS_KEY;
    const {
        highlightsData,
        payload: highlightsPayload,
        loading: highlightsLoading,
        error: highlightsError,
        fetchHighlights,
    } = useIntelligenceInsightsHighlights(projectId, {
        enabled: highlightsEnabled,
        analysisContext,
        analysisId: historicalAnalysisId,
    });

    const activeApiModule = API_MODULES[selectedKey] ? selectedKey : null;
    const {
        moduleData,
        payload: modulePayload,
        loading: moduleLoading,
        error: moduleError,
        fetchModule,
    } = useIntelligenceInsightsModule(projectId, activeApiModule, {
        enabled: Boolean(activeApiModule),
        analysisContext,
        analysisId: historicalAnalysisId,
    });

    const modules = overviewData.modules || [];
    const selectedModule = useMemo(
        () => modules.find(m => m.key === selectedKey) || null,
        [modules, selectedKey],
    );

    const shellReady = overviewData.shellSources?.ready ?? null;
    const shellProcessing = overviewData.shellSources?.processing ?? null;
    const generationWasActiveRef = useRef(false);
    const skipNextCompleteToastRef = useRef(false);

    useEffect(() => {
        const active = Boolean(overviewPayload?.data?.generation_progress?.active);
        if (generationWasActiveRef.current && !active) {
            if (skipNextCompleteToastRef.current) {
                skipNextCompleteToastRef.current = false;
            } else {
                const status = overviewPayload?.analysis?.status;
                if (status === 'complete' || status === 'complete_with_warnings') {
                    toast.success('Intelligence Insights version is ready.');
                    void fetchHighlights();
                    if (activeApiModule) void fetchModule();
                }
            }
        }
        generationWasActiveRef.current = active;
    }, [
        overviewPayload,
        activeApiModule,
        fetchHighlights,
        fetchModule,
    ]);

    const handleSelect = key => {
        setModuleFocus(null);
        if (key === OVERVIEW_KEY) {
            setHistoricalView(null);
        }
        setSelectedKey(key);
    };

    const handleExitHistoricalView = useCallback(() => {
        setHistoricalView(null);
    }, []);

    const handleOpenVersion = useCallback(version => {
        if (!version) return;
        if (version.status === 'generating') {
            toast.info('This analysis version is still generating.');
            return;
        }
        if (version.status === 'current') {
            setHistoricalView(null);
            setSelectedKey('management_summary');
            return;
        }
        setHistoricalView({
            analysisId: version.id || version.analysisId,
            version: version.version,
            label: version.label,
            meta: version.meta,
            status: version.status,
        });
        setSelectedKey('management_summary');
    }, []);

    const handleOpenModule = (key, focus = null) => {
        setModuleFocus(focus || null);
        setSelectedKey(key);
    };

    const handleGenerate = async () => {
        if (demoMode) {
            toast.info('Demo mode is on — API generate is disabled.');
            return;
        }
        try {
            skipNextCompleteToastRef.current = true;
            const { contextType, contextLabel, workstreamIds } = analysisContext;
            await generateAnalysis({
                contextType,
                contextLabel,
                workstreamIds,
            });
            toast.success(
                'Analysis version generated — all insight modules (Highlights through Evidence & Follow-Ups) are ready.',
            );
            setHistoricalView(null);
            setSelectedKey(OVERVIEW_KEY);
            try {
                await fetchHighlights();
            } catch {
                // Overview already refreshed; Highlights will load on tab open.
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Analysis generation failed.');
        }
    };

    const handleGoToOverview = useCallback(() => {
        setSelectedKey(OVERVIEW_KEY);
    }, []);

    const handleReviewSources = useCallback((focus = {}) => {
        setOverviewFocus({
            drawer: 'sources',
            tab: focus.tab || 'All',
            sourceId: focus.sourceId || null,
        });
        setSelectedKey(OVERVIEW_KEY);
    }, []);

    const handleClearOverviewFocus = useCallback(() => {
        setOverviewFocus(null);
    }, []);

    const resolveReviewSourcesFocus = useCallback(() => {
        if (moduleData?.sourceReadiness) {
            return buildSourceReadinessReviewFocus(moduleData.sourceReadiness);
        }
        if (overviewData?.secondaryViews?.sourceReadiness) {
            return buildSourceReadinessReviewFocus(overviewData.secondaryViews.sourceReadiness);
        }
        return { tab: 'Failed', sourceId: null };
    }, [moduleData, overviewData]);

    const handleNewEvidenceAction = useCallback(
        async actionKey => {
            if (actionKey === 'generate_new_version' || actionKey === 'generate_analysis') {
                await handleGenerate();
                return;
            }
            if (
                actionKey === 'review_sources' ||
                actionKey === 'review_evidence' ||
                actionKey === 'review_failed'
            ) {
                handleReviewSources(resolveReviewSourcesFocus());
            }
        },
        [handleGenerate, handleReviewSources, resolveReviewSourcesFocus],
    );
    const handleBannerAction = async actionKey => {
        if (actionKey === 'generate_new_version' || actionKey === 'generate_v4' || actionKey === 'retry') {
            await handleGenerate();
            return;
        }
        if (actionKey === 'open_analysis') {
            setSelectedKey('management_summary');
            return;
        }
        if (actionKey === 'review_failed') {
            handleReviewSources(resolveReviewSourcesFocus());
            return;
        }
        if (actionKey === 'review_sources' || actionKey === 'review_evidence') {
            handleReviewSources(resolveReviewSourcesFocus());
        }
    };

    const handleOpenChangeContext = useCallback(() => {
        setContextModalOpen(true);
    }, []);

    const handleApplyChooseContext = useCallback(
        ({ mode, selectedWorkstreams: nextWs, customContext: nextCustom }) => {
            if (mode === CONTEXT_MODE.CUSTOM) {
                setCustomContext(nextCustom);
                setContextMode(CONTEXT_MODE.CUSTOM);
            } else if (mode === CONTEXT_MODE.SELECT_WORKSTREAMS) {
                setSelectedWorkstreams(nextWs || []);
                setContextMode(CONTEXT_MODE.SELECT_WORKSTREAMS);
            } else {
                setSelectedWorkstreams(
                    nextWs?.length ? nextWs : workstreamOptionsFromList(workstreams),
                );
                setContextMode(CONTEXT_MODE.ENTIRE_PROJECT);
            }
            setContextModalOpen(false);
        },
        [
            setContextMode,
            setCustomContext,
            setSelectedWorkstreams,
            workstreams,
        ],
    );

    const handleAskAi = useCallback(
        (extras = {}) => {
            const extra =
                extras && typeof extras === 'object' && !extras.nativeEvent ? extras : {};
            const analysisPayload =
                selectedKey === HIGHLIGHTS_KEY
                    ? highlightsPayload
                    : API_MODULES[selectedKey]
                      ? modulePayload
                      : overviewPayload;
            const analysis = analysisPayload?.analysis || overviewPayload?.analysis || {};
            const context = analysisPayload?.context || overviewPayload?.context || {};
            const moduleTitle =
                extra.moduleTitle ||
                selectedModule?.title ||
                INSIGHT_MODULES.find(m => m.key === selectedKey)?.title;
            const origin = {
                ...buildInsightsAskLensOrigin({
                    moduleKey: selectedKey,
                    moduleTitle,
                    analysis,
                    context,
                    analysisContext,
                    scopeArea: extra.scopeArea || null,
                    scopeAreaLabel: extra.scopeAreaLabel || null,
                    modulePayload: analysisPayload || overviewPayload || null,
                }),
                openedAt: Date.now(),
            };
            dispatch(clearAskAiChat());
            dispatch(setInsightsAskOrigin(origin));
            navigate('/lens-intelligence', { state: { insightsAskOrigin: origin } });
        },
        [
            analysisContext,
            dispatch,
            highlightsPayload,
            modulePayload,
            navigate,
            overviewPayload,
            selectedKey,
            selectedModule,
        ],
    );

    const handleExportPdf = useCallback(async () => {
        const data = selectedKey === HIGHLIGHTS_KEY ? highlightsData : moduleData;
        if (!canExportInsightsModule(data)) return;
        try {
            await exportIntelligenceInsightsPdf(data);
            toast.success('Exported to PDF');
        } catch {
            toast.error('Could not export PDF');
        }
    }, [highlightsData, moduleData, selectedKey]);

    const exportEnabled = useMemo(() => {
        const data = selectedKey === HIGHLIGHTS_KEY ? highlightsData : moduleData;
        return canExportInsightsModule(data);
    }, [highlightsData, moduleData, selectedKey]);

    const ModuleComponent = API_MODULES[selectedKey] || null;

    return (
        <LensIntelligenceShell
            activeTab="insights"
            sourcesReady={shellReady}
            sourcesProcessing={shellProcessing}>
            <div className={styles.insightsPageV2}>
                <aside className={styles.insightsSidebarV2}>
                    <IntelligenceInsightsNav
                        modules={modules}
                        selectedKey={selectedKey}
                        onSelect={handleSelect}
                        moduleCount={modules.length || 8}
                    />
                </aside>

                <main className={styles.insightsMainV2}>
                    <InsightsGeneratingOverlay
                        active={generating || Boolean(overviewPayload?.data?.generation_progress?.active)}
                        progress={overviewPayload?.data?.generation_progress}
                    />
                    <InsightsReadOnlyProvider readOnly={readOnly}>
                    {error && selectedKey === OVERVIEW_KEY ? (
                        <div className={styles.modulePlaceholder}>
                            <div className={styles.modulePlaceholderCard}>
                                <p className={styles.modulePlaceholderEyebrow}>Overview</p>
                                <h2 className={styles.modulePlaceholderTitle}>Could not load insights</h2>
                                <p className={styles.modulePlaceholderDesc}>{error}</p>
                                <button
                                    type="button"
                                    className={styles.modulePlaceholderBtn}
                                    onClick={() => fetchOverview()}>
                                    Retry
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {!error && selectedKey === OVERVIEW_KEY ? (
                        <IntelligenceInsightsOverview
                            data={overviewData}
                            variant={demoMode ? demoVariant : overviewData.variant}
                            onVariantChange={demoMode ? setDemoVariant : undefined}
                            demoOptions={demoMode ? OVERVIEW_VARIANT_OPTIONS : null}
                            onOpenModule={handleOpenModule}
                            onOpenVersion={handleOpenVersion}
                            onGenerate={handleGenerate}
                            onBannerAction={handleBannerAction}
                            onOpenChangeContext={handleOpenChangeContext}
                            sourceReadinessFocus={
                                overviewFocus?.drawer === 'sources' ? overviewFocus : null
                            }
                            onSourceReadinessFocusConsumed={handleClearOverviewFocus}
                            loading={loading}
                            generating={generating}
                        />
                    ) : null}

                    {selectedKey === HIGHLIGHTS_KEY ? (
                        <IntelligenceInsightsHighlights
                            projectId={projectId}
                            data={highlightsData}
                            loading={highlightsLoading}
                            generating={generating}
                            error={highlightsError}
                            onRetry={fetchHighlights}
                            onGenerateAnalysis={handleGenerate}
                            onGoToOverview={handleGoToOverview}
                            onNewEvidenceAction={handleNewEvidenceAction}
                            onOpenModule={handleOpenModule}
                            onReviewFailedSource={() => handleReviewSources({ tab: 'Failed' })}
                            onAskAi={handleAskAi}
                            onExport={handleExportPdf}
                            exportEnabled={exportEnabled}
                            readOnly={readOnly}
                            historicalVersion={historicalView}
                            onExitHistoricalView={handleExitHistoricalView}
                        />
                    ) : null}

                    {ModuleComponent
                        ? React.createElement(ModuleComponent, {
                              projectId,
                              data: moduleData,
                              loading: moduleLoading,
                              error: moduleError,
                              generating,
                              onOpenModule: handleOpenModule,
                              onReviewFailedSource: focus =>
                                  handleReviewSources(focus || resolveReviewSourcesFocus()),
                              onAskAi: handleAskAi,
                              onExport: handleExportPdf,
                              exportEnabled,
                              onGenerateAnalysis: handleGenerate,
                              onGoToOverview: handleGoToOverview,
                              onNewEvidenceAction: handleNewEvidenceAction,
                              onRetry: fetchModule,
                              readOnly,
                              historicalVersion: historicalView,
                              onExitHistoricalView: handleExitHistoricalView,
                              initialScopeArea:
                                  selectedKey === 'key_findings'
                                      ? moduleFocus?.scopeArea || null
                                      : null,
                              initialEvidenceMatterId:
                                  selectedKey === 'evidence_follow_ups'
                                      ? moduleFocus?.evidenceMatterId || null
                                      : null,
                          })
                        : null}

                    {!error &&
                    selectedKey !== OVERVIEW_KEY &&
                    selectedKey !== HIGHLIGHTS_KEY &&
                    !ModuleComponent ? (
                        <div className={styles.modulePlaceholder}>
                            <div className={styles.modulePlaceholderCard}>
                                <p className={styles.modulePlaceholderEyebrow}>Module preview</p>
                                <h2 className={styles.modulePlaceholderTitle}>
                                    {selectedModule?.title || 'Insight module'}
                                </h2>
                                <p className={styles.modulePlaceholderDesc}>
                                    {selectedModule?.description ||
                                        'Module content will be wired in a later pass.'}
                                </p>
                                <button
                                    type="button"
                                    className={styles.modulePlaceholderBtn}
                                    onClick={() => handleSelect(OVERVIEW_KEY)}>
                                    ← Back to Overview
                                </button>
                            </div>
                        </div>
                    ) : null}
                    </InsightsReadOnlyProvider>
                </main>
            </div>

            <AskAiChooseContextModal
                open={contextModalOpen}
                onClose={() => setContextModalOpen(false)}
                onApply={handleApplyChooseContext}
                initialMode={contextMode}
                initialWorkstreams={selectedWorkstreams}
                initialCustomContext={customContext}
                workstreams={workstreams}
                projectId={projectId}
                scopeStats={scopeStats}
                indexingFiles={indexingFiles}
            />
        </LensIntelligenceShell>
    );
};

export default IntelligenceInsights;
